/**
 * P4: drawing on the screenshot. Pure: every shape is painted through the
 * small Penseel interface, which a CanvasRenderingContext2D satisfies and a
 * test can record. The shapes are painted INTO the image that is sent, so a
 * blacked-out block is gone from the pixels and there is only one image.
 */
export type Gereedschap = 'kader' | 'pijl' | 'pen' | 'weglakken' | 'pin';
export const GEREEDSCHAPPEN: readonly Gereedschap[] = ['kader', 'pijl', 'pen', 'weglakken', 'pin'];

export interface Punt { x: number; y: number }
export type Vorm =
  | { soort: 'kader' | 'pijl' | 'weglakken'; van: Punt; tot: Punt }
  | { soort: 'pen'; punten: Punt[] }
  | { soort: 'pin'; op: Punt };

export const KLEUR = '#e5484d';
export const ZWART = '#000000';
export const PIN_STRAAL = 14;
const PIJLPUNT = 14;

export interface Penseel {
  strokeStyle: string | CanvasGradient | CanvasPattern;
  fillStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  fill(): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  fillText(tekst: string, x: number, y: number): void;
  drawImage(beeld: CanvasImageSource, x: number, y: number): void;
}

function vak(a: Punt, b: Punt): [number, number, number, number] {
  return [Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y)];
}

export function tekenVorm(p: Penseel, v: Vorm): void {
  p.lineWidth = 3;
  switch (v.soort) {
    case 'weglakken':
      p.fillStyle = ZWART;
      p.fillRect(...vak(v.van, v.tot));
      return;
    case 'kader':
      p.strokeStyle = KLEUR;
      p.strokeRect(...vak(v.van, v.tot));
      return;
    case 'pijl': {
      const hoek = Math.atan2(v.tot.y - v.van.y, v.tot.x - v.van.x);
      p.strokeStyle = KLEUR;
      p.beginPath();
      p.moveTo(v.van.x, v.van.y);
      p.lineTo(v.tot.x, v.tot.y);
      for (const zij of [-1, 1]) {
        p.moveTo(v.tot.x, v.tot.y);
        p.lineTo(
          v.tot.x - PIJLPUNT * Math.cos(hoek + (zij * Math.PI) / 6),
          v.tot.y - PIJLPUNT * Math.sin(hoek + (zij * Math.PI) / 6),
        );
      }
      p.stroke();
      return;
    }
    case 'pen': {
      const [eerste, ...rest] = v.punten;
      if (!eerste || rest.length === 0) return;
      p.strokeStyle = KLEUR;
      p.beginPath();
      p.moveTo(eerste.x, eerste.y);
      for (const q of rest) p.lineTo(q.x, q.y);
      p.stroke();
      return;
    }
    case 'pin':
      p.fillStyle = KLEUR;
      p.beginPath();
      p.arc(v.op.x, v.op.y, PIN_STRAAL, 0, 2 * Math.PI);
      p.fill();
      p.fillStyle = '#ffffff';
      p.font = 'bold 16px sans-serif';
      p.textAlign = 'center';
      p.textBaseline = 'middle';
      p.fillText('1', v.op.x, v.op.y);
      return;
  }
}

export function tekenAlles(
  p: Penseel,
  basis: CanvasImageSource & { width: number; height: number },
  vormen: readonly Vorm[],
): void {
  p.clearRect(0, 0, basis.width, basis.height);
  p.drawImage(basis, 0, 0);
  for (const v of vormen) tekenVorm(p, v);
}

/** A box, arrow or black-out with no extent (a click without a drag) is not a shape. */
export function isLeeg(v: Vorm): boolean {
  return v.soort !== 'pen' && v.soort !== 'pin' && v.van.x === v.tot.x && v.van.y === v.tot.y;
}

export function metPin(vormen: readonly Vorm[], op: Punt): Vorm[] {
  return [...vormen.filter((v) => v.soort !== 'pin'), { soort: 'pin', op }];
}

export function ongedaan(vormen: readonly Vorm[]): Vorm[] {
  return vormen.slice(0, -1);
}

export function platSlaan(basis: HTMLCanvasElement, vormen: readonly Vorm[]): HTMLCanvasElement | null {
  const uit = document.createElement('canvas');
  uit.width = basis.width;
  uit.height = basis.height;
  const ctx = uit.getContext('2d');
  if (!ctx) return null;
  tekenAlles(ctx, basis, vormen);
  return uit;
}
